import {useCallback, useEffect, useState} from 'react';
import useCustomerMgr from './useCustomerMgr';
import useDeferredAction from './useDeferredAction';
import Customer from 'coreClasses/customer/Customer';
import ProductModel from 'coreClasses/product/ProductModel';
import VariantModel from 'coreClasses/product/VariantModel';

export interface ProductBanFlags {
	isBannedA: boolean;
	isBannedB: boolean;
	isBanned: boolean;
}

export interface ProductsBannedResult {
	banned: Array<ProductModel | VariantModel>;
	notBanned: Array<ProductModel | VariantModel>;
	bansByProductId: Record<string, ProductBanFlags>;
}

/**
 * useBannedProducts checks a list of products against the current customer's groups
 * and returns the split arrays plus a map of ban flags per product id.
 */
export default (products: Array<ProductModel | VariantModel> = []): ProductsBannedResult => {
	const customerMgr = useCustomerMgr();
	const [result, setResult] = useState<ProductsBannedResult>({
		banned: [],
		notBanned: [],
		bansByProductId: {},
	});

	const areProductsReady = (items: Array<ProductModel | VariantModel>): boolean => {
		if (!items || items.length === 0) {
			return true;
		}

		return items.every((p) => Boolean(p?.isReady?.()));
	};

	const conditionFn = useCallback(
		(
			customer: Customer,
			items: Array<ProductModel | VariantModel>,
			customerReady: boolean,
			productsReady: boolean,
		) => {
			return Boolean(customer) && Array.isArray(items) && customerReady && productsReady;
		},
		[]);

	const currentCustomer = customerMgr.getCurrentCustomer();
	const customerReady = Boolean(currentCustomer?.isReady?.());
	const productsReady = areProductsReady(products);

	const {deferAction} = useDeferredAction(
		[currentCustomer, products, customerReady, productsReady],
		conditionFn,
	);

	useEffect(() => {
		if (products.length === 0) {
			return;
		}

		deferAction((customer: Customer, items: Array<ProductModel | VariantModel>) => {
			try {
				if (!items || items.length === 0) {
					setResult({banned: [], notBanned: [], bansByProductId: {}});

					return;
				}

				const bansByProductId: Record<string, ProductBanFlags> = {};

				const evaluateBan = (product: ProductModel | VariantModel): ProductBanFlags => {
					const groupA = (product as any)?.c_availableForCustomerGroupA as string | undefined;
					const groupB = (product as any)?.c_availableForCustomerGroupB as string | undefined;

					const isBannedA = groupA ? !customer.isMemberOfCustomerGroup(groupA) : false;
					const isBannedB = groupB ? !customer.isMemberOfCustomerGroup(groupB) : false;

					return {
						isBannedA,
						isBannedB,
						isBanned: isBannedA || isBannedB,
					};
				};

				const banned: Array<ProductModel | VariantModel> = [];
				const notBanned: Array<ProductModel | VariantModel> = [];

				for (const product of items) {
					const flags = evaluateBan(product);
					const productId = (product as any)?.getProductId?.();

					if (productId) {
						bansByProductId[productId] = flags;
					}

					if (flags.isBanned) {
						banned.push(product);
					} else {
						notBanned.push(product);
					}
				}

				setResult({banned, notBanned, bansByProductId});
			} catch (error) {
				setResult({banned: [], notBanned: [], bansByProductId: {}});
			}
		});
	}, [products]);

	return result;
};